import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import Icon, { type IconName } from './Icon.js';

/**
 * iOS-style swipe actions for a list row (Mail / Reminders).
 *
 * - Swipe right reveals the leading action; past the commit distance it runs
 *   on release (a "full swipe"), e.g. Complete.
 * - Swipe left reveals the trailing actions as buttons; the row stays open
 *   until one is tapped or the row is tapped / swiped back.
 *
 * Touch and pen only: a mouse never drags rows (text stays selectable), so
 * every action here must ALSO be reachable another way (the row's ⋯ menu on
 * a pointer device). `touch-action: pan-y` hands vertical scrolling to the
 * browser, which cancels the gesture when the user scrolls instead.
 */

export interface SwipeAction {
  label: string;
  icon?: IconName;
  tone?: 'good' | 'warn' | 'bad' | 'accent' | 'neutral';
  onAction: () => void;
}

const ACTION_W = 76;
const COMMIT = 0.42; // of the row width, for a full leading swipe
const LONG_PRESS_MS = 480;

function vibrate(): void {
  try {
    navigator.vibrate?.(8);
  } catch {
    /* unsupported */
  }
}

export default function Swipeable({
  leading,
  trailing = [],
  onLongPress,
  className,
  style,
  children,
}: {
  leading?: SwipeAction;
  trailing?: readonly SwipeAction[];
  onLongPress?: () => void;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}): JSX.Element {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const host = useRef<HTMLLIElement>(null);
  const g = useRef<{ x: number; y: number; base: number; id: number; axis: 'x' | 'y' | null; moved: boolean } | null>(null);
  const press = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressClick = useRef(false);
  const openW = trailing.length * ACTION_W;
  const enabled = Boolean(leading) || trailing.length > 0;

  const clearPress = (): void => {
    if (press.current) clearTimeout(press.current);
    press.current = null;
  };

  // An open row closes when anything else on the page is touched.
  useEffect(() => {
    if (dx >= 0 || dragging) return;
    const close = (e: PointerEvent): void => {
      if (!host.current?.contains(e.target as Node)) setDx(0);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [dx, dragging]);

  useEffect(() => clearPress, []);

  function onPointerDown(e: React.PointerEvent): void {
    if (e.pointerType === 'mouse') return;
    g.current = { x: e.clientX, y: e.clientY, base: dx, id: e.pointerId, axis: null, moved: false };
    if (onLongPress) {
      press.current = setTimeout(() => {
        press.current = null;
        if (g.current && !g.current.moved) {
          suppressClick.current = true;
          vibrate();
          onLongPress();
          g.current = null;
        }
      }, LONG_PRESS_MS);
    }
  }

  function onPointerMove(e: React.PointerEvent): void {
    const s = g.current;
    if (!s || s.id !== e.pointerId) return;
    const mx = e.clientX - s.x;
    const my = e.clientY - s.y;
    if (!s.moved && Math.hypot(mx, my) > 6) {
      s.moved = true;
      clearPress();
    }
    if (!enabled) return;
    if (s.axis === null && Math.hypot(mx, my) > 8) s.axis = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
    if (s.axis !== 'x') return;
    if (!dragging) {
      setDragging(true);
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    }
    let next = s.base + mx;
    if (!leading) next = Math.min(next, 0);
    if (trailing.length === 0) next = Math.max(next, 0);
    // Rubber-band past the open width.
    if (next < -openW) next = -openW + (next + openW) * 0.3;
    setDx(next);
  }

  function onPointerUp(e: React.PointerEvent): void {
    clearPress();
    const s = g.current;
    g.current = null;
    if (!s || s.id !== e.pointerId) return;
    if (s.axis !== 'x') return;
    suppressClick.current = true;
    setDragging(false);
    const width = host.current?.offsetWidth || 360;
    if (leading && dx > width * COMMIT) {
      vibrate();
      setDx(0);
      leading.onAction();
      return;
    }
    if (dx < -openW / 2) setDx(-openW);
    else setDx(0);
  }

  function onPointerCancel(): void {
    clearPress();
    g.current = null;
    setDragging(false);
    setDx((v) => (v < -openW / 2 ? -openW : 0));
  }

  const run = (a: SwipeAction) => (): void => {
    setDx(0);
    a.onAction();
  };

  const width = host.current?.offsetWidth || 360;
  const armed = leading !== undefined && dx > width * COMMIT;

  return (
    <li
      ref={host}
      className={`ui-swipe${dx !== 0 ? ' ui-swipe--moving' : ''}${className ? ` ${className}` : ''}`}
      style={style}
    >
      {leading && dx > 0 && (
        <div className={`ui-swipe__lead ui-swipe--${leading.tone ?? 'good'}${armed ? ' ui-swipe__lead--armed' : ''}`} aria-hidden="true">
          {leading.icon && <Icon name={leading.icon} size={22} />}
          <span>{leading.label}</span>
        </div>
      )}
      {trailing.length > 0 && dx < 0 && (
        <div className="ui-swipe__trail" style={{ width: Math.max(openW, -dx) }}>
          {trailing.map((a) => (
            <button
              key={a.label}
              type="button"
              tabIndex={-1}
              className={`ui-swipe__btn ui-swipe--${a.tone ?? 'neutral'}`}
              onClick={run(a)}
            >
              {a.icon && <Icon name={a.icon} size={20} />}
              <span>{a.label}</span>
            </button>
          ))}
        </div>
      )}
      <div
        className="ui-swipe__body"
        style={{
          transform: dx ? `translate3d(${dx}px,0,0)` : undefined,
          transition: dragging ? 'none' : undefined,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onContextMenu={onLongPress ? (e) => e.preventDefault() : undefined}
        onClickCapture={(e) => {
          if (suppressClick.current) {
            suppressClick.current = false;
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (dx < 0) {
            e.preventDefault();
            e.stopPropagation();
            setDx(0);
          }
        }}
      >
        {children}
      </div>
    </li>
  );
}
